import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import moment from "moment";

import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import ClaimedIcon from "../../img/Claimed.svg";
import SlowClap from "../../components/SlowClap/SlowClap";
import CommentList from "../../components/Comments/CommentList";
import { UseUserDetails } from "../../hooks/auth";
import { ADD_REACT, DELETE_REACT, FETCH_REACT } from "../../api/react";
import { ADD_COMMENT, UPDATE_COMMENT, DELETE_COMMENT } from "../../api/comments";
import { REACTION_PROPERTY_TYPE } from "../../constants/reactions";
import socket from "../../configs/socket";
import socketConstants from "../../constants/socket";

const { SOCKET_EVENT } = socketConstants;

/* Shared design tokens — kept in sync with FeedCard so review-claim cards sit
   visually alongside the job cards in the Team Feed. */
const TOKENS = {
  surface: "#FFFFFF",
  line: "#E7E9EE",
  hairline: "#EFF1F5",
  ink: "#0B1320",
  ink2: "#2A3142",
  muted: "#6B7388",
  faint: "#9AA1B0",
  gold: "#F0B33D",
  goldSoft: "#FFF7E6",
  goldText: "#8A5A05",
  quoteBg: "#F8FAFD",
};

const FONT = '"DM Sans", "Inter", "Arial", sans-serif';
const NUM_FEATURES = '"tnum" 1, "lnum" 1';

const Stars = ({ rating }) => {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  if (!r) return null;
  return (
    <Box sx={{ display: "inline-flex", gap: "2px", color: TOKENS.gold }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          // eslint-disable-next-line react/no-array-index-key
          key={`star-${i}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill={i < r ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <path d="M12 2l2.9 6.26 6.1.55-4.6 4.04 1.36 6.15L12 16.9 6.24 19l1.36-6.15L3 8.81l6.1-.55L12 2z" />
        </svg>
      ))}
    </Box>
  );
};

/**
 * Standalone Team Feed card for a claimed review.
 * Shows who claimed it (header) and the reviewer/customer name + review text
 * in place of the job photo/details. Intentionally omits job total, time,
 * tags, location and the photo uploader.
 */
export default function FeedReviewCard({ item, elementId, autoOpenComments }) {
  const currentUser = UseUserDetails();
  const claimer = item?.user || (item?.claimers || [])[0];
  const claimerName =
    `${claimer?.firstName || ""} ${claimer?.lastName || ""}`.trim() || "Teammate";
  const others = Math.max(0, (item?.claimers?.length || 0) - 1);
  const claimerIds = (item?.claimers || []).map((c) => c._id).filter(Boolean);
  const points = Math.floor(item?.points || 0);
  const reviewerName = item?.name || "Customer";
  const reviewText = item?.content;
  const timeAgo = moment(item?.claimedAt || item?.extRefDate || item?.updatedAt).fromNow();

  /* ---- Reactions / comments (wired to the Review, not a Job) ---- */
  const [reacts, setReacts] = useState(item?.reacts || []);
  const pendingReactAdds = useRef({});
  const pendingCommentReactAdds = useRef({});
  const [comments, setComments] = useState(item?.comments || []);
  const [showComments, setShowComments] = useState(!!autoOpenComments);

  useEffect(() => setReacts(item?.reacts || []), [item?.reacts]);
  useEffect(() => setComments(item?.comments || []), [item?.comments]);

  const refreshReacts = async () => {
    try {
      const { data } = await FETCH_REACT(item._id);
      setReacts(data?.reacts || []);
    } catch (err) {
      /* keep current state on failure */
    }
  };

  // Shared by the clap (review-level) and comment favorites. SlowClap always
  // passes the Job property type, so anything that isn't a Comment react is
  // treated as a Review react here. Job cards refresh via getJob(); reviews have
  // no equivalent, so we refetch review reacts and patch comment reacts locally.
  const onReactToggle = async (propertyId, userId, isActive, react, iconType, propertyModel) => {
    const isComment = propertyModel === REACTION_PROPERTY_TYPE.COMMENT;
    const model = isComment ? "Comment" : "Review";

    if (isComment) {
      // Optimistic UI: patch this comment's reacts instantly, don't wait on
      // the ADD_REACT/DELETE_REACT round trip to paint the highlight.
      const tempId = `temp_${userId}_${Date.now()}`;
      setComments((prev) =>
        prev.map((c) => {
          if (c._id !== propertyId) return c;
          const existing = c.reacts || [];
          if (isActive) {
            return { ...c, reacts: [...existing, { _id: tempId, users: [currentUser] }] };
          }
          return { ...c, reacts: existing.filter((r) => String(r.users?.[0]?._id) !== String(userId)) };
        }),
      );
      if (isActive) {
        socket.emit(SOCKET_EVENT.POST_COMMENT_LIKE, {
          sender: userId,
          commentId: propertyId,
          targetId: item._id,
          targetType: "Review",
        });
      }
      try {
        if (isActive) {
          const addPromise = ADD_REACT({ propertyId, data: { userId, propertyModel: model, iconType } }).then(
            ({ data }) => data,
          );
          pendingCommentReactAdds.current[tempId] = addPromise;
          const savedReact = await addPromise;
          setComments((prev) =>
            prev.map((c) => {
              if (c._id !== propertyId) return c;
              return {
                ...c,
                reacts: (c.reacts || []).map((r) =>
                  r._id === tempId ? { ...(savedReact || {}), users: [currentUser] } : r,
                ),
              };
            }),
          );
        } else if (react?._id) {
          const pendingAdd = pendingCommentReactAdds.current[react._id];
          const realId = pendingAdd ? (await pendingAdd)._id : react._id;
          await DELETE_REACT(realId);
        }
      } catch (err) {
        // resync this comment's reacts from the last known server state on failure
        setComments((prev) =>
          prev.map((c) => (c._id === propertyId ? { ...c, reacts: item?.comments?.find((x) => x._id === propertyId)?.reacts || [] } : c)),
        );
      } finally {
        delete pendingCommentReactAdds.current[tempId];
      }
      return;
    }

    // Review-level clap — same optimistic pattern, reconciled via refetch.
    const tempId = `temp_${userId}_${Date.now()}`;
    if (isActive) {
      setReacts((prev) => [...prev, { _id: tempId, users: [{ _id: userId }] }]);
      socket.emit(SOCKET_EVENT.POST_SLOW_CLAP, {
        sender: userId,
        targetId: item._id,
        targetType: "Review",
        recipientIds: claimerIds,
      });
    } else if (react?._id) {
      setReacts((prev) => prev.filter((r) => r._id !== react._id));
    }
    try {
      if (isActive) {
        const addPromise = ADD_REACT({ propertyId, data: { userId, propertyModel: model, iconType } });
        pendingReactAdds.current[tempId] = addPromise;
        await addPromise;
      } else if (react?._id) {
        const pendingAdd = pendingReactAdds.current[react._id];
        const realId = pendingAdd ? (await pendingAdd).data?._id : react._id;
        await DELETE_REACT(realId);
      }
      refreshReacts();
    } catch (err) {
      refreshReacts();
    } finally {
      delete pendingReactAdds.current[tempId];
    }
  };

  const onAddComment = async (_propertyId, senderId, content) => {
    if (!content?.trim()) return;
    const { data: comment } = await ADD_COMMENT({
      jobId: item._id,
      data: { userId: senderId, content, propertyModel: "Review" },
    });
    setComments((prev) => [comment, ...prev]);
    socket.emit(SOCKET_EVENT.POST_COMMENT, {
      sender: senderId,
      targetId: item._id,
      targetType: "Review",
      content,
      recipientIds: claimerIds,
    });
  };

  const onEditComment = async (commentId, content) => {
    const { data: updated } = await UPDATE_COMMENT(commentId, content);
    setComments((prev) => prev.map((c) => (c._id === commentId ? updated : c)));
  };

  const onDeleteComment = async (commentId) => {
    await DELETE_COMMENT(commentId);
    setComments((prev) => prev.filter((c) => c._id !== commentId));
  };

  return (
    <Box
      id={elementId}
      sx={{
        background: TOKENS.surface,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow:
          "0 1px 0 rgba(255,255,255,0.6) inset, 0 1px 2px rgba(15,23,42,0.04), 0 18px 36px rgba(15,23,42,0.07)",
        marginBottom: "22px",
        fontFamily: FONT,
        color: TOKENS.ink,
      }}
    >
      {/* HEADER — who claimed the review */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "18px 22px 14px",
          background: "linear-gradient(180deg, #FFFBF2 0%, #FFFFFF 100%)",
          borderBottom: `1px solid ${TOKENS.hairline}`,
        }}
      >
        <Box sx={{ flexShrink: 0, boxShadow: "0 0 0 2px #fff, 0 0 0 3px rgba(240,179,61,0.28)", borderRadius: "50%" }}>
          <SoftProfile src={claimer?.image} size="md" name={claimer?.firstName} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontWeight: 800, fontSize: "17px", letterSpacing: "-0.01em", color: TOKENS.ink, lineHeight: 1.2 }}>
            {claimerName}
            {others > 0 && (
              <Box component="span" sx={{ fontWeight: 600, color: TOKENS.muted, fontSize: "13px" }}>
                {` & ${others} other${others > 1 ? "s" : ""}`}
              </Box>
            )}
          </Box>
          {/* "Claimed a review" pill */}
          <Box
            sx={{
              marginTop: "6px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              height: "18px",
              padding: "0 8px",
              borderRadius: "999px",
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              background: TOKENS.goldSoft,
              color: TOKENS.goldText,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.9 6.26 6.1.55-4.6 4.04 1.36 6.15L12 16.9 6.24 19l1.36-6.15L3 8.81l6.1-.55L12 2z" />
            </svg>
            Claimed a review
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0 }}>
          {points > 0 && (
            <Box
              sx={{
                fontFamily: FONT,
                fontFeatureSettings: NUM_FEATURES,
                display: "inline-flex",
                alignItems: "baseline",
                gap: "4px",
                background: "linear-gradient(180deg, #16A34A 0%, #15803D 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              <Box component="span" sx={{ fontSize: "18px", fontWeight: 700 }}>+{points}</Box>
              <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.02em" }}>pts</Box>
            </Box>
          )}
          <Box sx={{ fontSize: "11px", color: TOKENS.muted, fontWeight: 500 }}>{timeAgo}</Box>
        </Box>
      </Box>

      {/* BODY — reviewer + review text (replaces the photo/job area).
          Carries the same "Claimed" watermark used on the Claims page. */}
      <Box
        sx={{
          padding: "18px 22px 22px",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${ClaimedIcon})`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "28%",
            opacity: 0.25,
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "10px" }}>
          <Box sx={{ fontWeight: 700, fontSize: "15px", color: TOKENS.ink, letterSpacing: "-0.01em" }}>
            {reviewerName}
          </Box>
          <Stars rating={item?.rating} />
        </Box>

        {reviewText ? (
          <Box
            sx={{
              position: "relative",
              // Transparent so the "Claimed" watermark reads through; keep just
              // the gold accent line for the quote feel.
              background: "transparent",
              borderLeft: `3px solid ${TOKENS.gold}`,
              padding: "2px 0 2px 14px",
              fontSize: "14px",
              lineHeight: 1.5,
              color: TOKENS.ink2,
              fontStyle: "italic",
            }}
          >
            “{reviewText}”
          </Box>
        ) : (
          <Box sx={{ fontSize: "13px", color: TOKENS.faint, fontStyle: "italic" }}>
            No review text provided.
          </Box>
        )}

        {item?.service && (
          <Box
            sx={{
              marginTop: "10px",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: TOKENS.muted,
              textTransform: "lowercase",
              "&::first-letter": { textTransform: "uppercase" },
            }}
          >
            {item.service}
          </Box>
        )}
      </Box>

      {/* FOOTER — claps + comments (same social actions as job cards) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "8px 12px",
          borderTop: `1px solid ${TOKENS.hairline}`,
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <SlowClap
            jobId={item._id}
            currentUserId={currentUser?._id}
            reacts={reacts}
            onToggle={onReactToggle}
            showDetails={false}
          />
        </Box>

        <Box
          component="button"
          onClick={() => setShowComments((v) => !v)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            height: "38px",
            padding: "0 14px",
            borderRadius: "10px",
            background: showComments ? TOKENS.quoteBg : "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: FONT,
            fontSize: "13px",
            fontWeight: 600,
            color: TOKENS.ink2,
            "&:hover": { background: TOKENS.quoteBg },
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a8 8 0 0 1-12 7l-5 1 1-5a8 8 0 1 1 16-3z" />
          </svg>
          <Box component="span" sx={{ color: TOKENS.muted, fontWeight: 600 }}>
            {comments.length}
          </Box>
        </Box>
      </Box>

      {/* Inline comment thread */}
      {showComments && (
        <Box sx={{ borderTop: `1px solid ${TOKENS.hairline}`, background: TOKENS.quoteBg, padding: "12px 22px 16px" }}>
          <CommentList
            jobId={item._id}
            comments={comments}
            currentUser={currentUser}
            onAddComment={onAddComment}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
            onToggleFavorite={onReactToggle}
          />
        </Box>
      )}
    </Box>
  );
}
